import { cssVariables, isLocalhostDevMode } from "@/utils/utils";
import { Button, Modal, Result, Timeline, type TimelineItemProps } from "antd";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import AntIcon from "../../../../common/components/icons/AntIcon";
import TimelineDotIcon from "../../../../common/components/icons/TimelineDotIcon";
import { ModalSizes } from "../../../../common/constants";
import { CONTRACT_ROUTE_PATHS } from "../../../contract/paths";
import { InsuranceContract } from "../../../contract/types";
import { GenStep } from "../enums";
import { GenResponse } from "../types";
import { checkIfStepsAreSuccessful } from "../utils";

interface Props {
  result?: GenResponse;
  onCancel?: () => void;
  onReturnToCalculationClick?: (contract: InsuranceContract) => void;
}

export const CalcGenResultModal = ({ result, onCancel, onReturnToCalculationClick }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const contractCreated = result && checkIfStepsAreSuccessful(result.steps, GenStep.CREATE_CONTRACT);
  let contractSaved = false;
  let attachmentsProcessed = false;
  let emailSent = false;
  let processFinalized = false;
  let allStepsSuccessful = false;

  if (contractCreated) {
    contractSaved = checkIfStepsAreSuccessful(result.steps, GenStep.SAVE_CLIENTS_AND_CONTRACT);
    attachmentsProcessed = checkIfStepsAreSuccessful(
      result.steps,
      GenStep.DOWNLOAD_ATTACHMENTS,
      GenStep.GENERATE_ATTACHMENTS,
      GenStep.SAVE_ATTACHMENTS,
    );
    emailSent = checkIfStepsAreSuccessful(result.steps, GenStep.SEND_EMAIL);
    processFinalized = checkIfStepsAreSuccessful(result.steps, GenStep.FINALIZE_PROCESS);
    allStepsSuccessful = contractSaved && attachmentsProcessed && emailSent && processFinalized;
  }

  const getItems = (): TimelineItemProps[] => [
    {
      dot: <TimelineDotIcon status="success" />,
      color: "green",
      children: t("calc.genResult.steps.createContract"),
    },
    {
      dot: <TimelineDotIcon status={contractSaved ? "success" : "error"} />,
      color: contractSaved ? "green" : "red",
      children: t("calc.genResult.steps.saveContract"),
    },
    {
      dot: <TimelineDotIcon status={attachmentsProcessed ? "success" : "error"} />,
      color: attachmentsProcessed ? "green" : "red",
      children: t("calc.genResult.steps.processAttachments"),
    },
    {
      dot: <TimelineDotIcon status={emailSent ? "success" : "error"} />,
      color: emailSent ? "green" : "red",
      children: t("calc.genResult.steps.sendEmail"),
    },
    {
      dot: <TimelineDotIcon status={processFinalized ? "success" : "error"} />,
      color: processFinalized ? "green" : "red",
      children: t("calc.genResult.steps.finalizeProcess"),
    },
  ];

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      title={t("calc.genResult.title")}
      open={contractCreated}
      closable={isLocalhostDevMode()}
      cancelButtonProps={undefined}
      footer={
        <>
          {onReturnToCalculationClick && result?.contract && (
            <Button
              type="dashed"
              icon={<AntIcon type="double-left" />}
              onClick={() => {
                if (result.contract) {
                  onReturnToCalculationClick(result.contract);
                }

                return;
              }}
            >
              {t("calc.genResult.returnToCalculation")}
            </Button>
          )}
          <Button
            type="primary"
            icon={<AntIcon type="file-done" />}
            onClick={() =>
              navigate(
                contractSaved
                  ? generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: result?.contract?.id })
                  : CONTRACT_ROUTE_PATHS.list.to,
                { replace: true },
              )
            }
          >
            {contractSaved ? t("calc.genResult.continueToContract") : t("calc.genResult.continueToContractsList")}
          </Button>
        </>
      }
      onCancel={onCancel}
      mask={{ closable: false }}
    >
      <Result
        style={{ padding: "0 32px" }}
        status={allStepsSuccessful ? "success" : "warning"}
        title={allStepsSuccessful ? t("calc.genResult.successDesc") : t("calc.genResult.errorDesc")}
        extra={
          <>
            {!allStepsSuccessful && (
              <div className="margin-bottom-large center-align">
                <div className="margin-bottom-small">
                  <b style={{ fontSize: 18, color: cssVariables.colorRed }}>{t("calc.genResult.errorInfoDesc1")}</b>
                </div>
                <div className="sub-header-info">{t("calc.genResult.errorInfoDesc2")}</div>
              </div>
            )}

            <div style={{ maxWidth: "350px", margin: "0 auto" }}>
              <Timeline items={getItems()} />
            </div>
          </>
        }
      />
    </Modal>
  );
};
